import * as React from 'react';
import { translate, TranslationFunction, Trans } from 'react-i18next';
import InfoCircleSmall from 'wix-ui-icons-common/InfoCircleSmall';
import WarningIcon from 'wix-ui-icons-common/StatusWarningFilled';
import { InviteStatusType, PermissionKey } from '../../services/server-types';
import { getNameTitle } from '../services/row-helper';
import TeamMemberActions from '../TeamMemberActions/TeamMemberActions';
import {
  isUserRowData,
  TeamMemberTableRowData,
  TeamTableRowType,
  isInviteRowData,
  isTeamRowData,
  TeamRowData
} from '../types';
import { UserImage } from '../UserImage/UserImage';
import * as css from './TeamMembersList.scss';
import { formatDate } from '../../services/date-helper';
import { Role, RolesData } from '../../types/roles';
import TeamMembersOwnerActionCell from './TeamMembersOwnerActionCell';
import { getOrBuildRoleByRoleKey } from '../../redux/selectors/roles';
import { TeamContributorImage } from '../TeamContributorImage/TeamContributorImage';
import { generateLearnMoreLink } from '../../consts/external-links';
import UserRemoveSmall from 'wix-ui-icons-common/UserRemoveSmall';
import { hasPermissionByLevel } from '../../services/permissions-helper';

import { Badge, Text, Tooltip, TextButton, Heading, InfoIcon, Box, Button } from 'wix-style-react';

export const renderRoleCell = (roles: Role[], t: TranslationFunction<any, object, string>) => (rowData: TeamMemberTableRowData) => {
  if (!rowData.isPendingOwner && (!rowData.roles || !rowData.roles.length)) {
    return;
  }

  let rolesData;
  if (rowData.isPendingOwner && !rowData.roles.length) {
    rolesData = [{ title: t('role.pendingOwner.title') }];
  } else {
    rolesData = rowData.roles.map(role => getOrBuildRoleByRoleKey(roles, role)).filter(role => role);
  }

  let visibleRoles = rolesData;
  if (rolesData.length > 2) {
    visibleRoles = rolesData.slice(0, 1);
  }

  const visibleRolesTemplate = visibleRoles.map(role => <SingleRole role={role} rowRole={role.id}/>);

  if (visibleRoles.length === rolesData.length) {
    return visibleRolesTemplate;
  }
  rolesData.shift();
  const restOfRolesTemplate = (
    <div className={css.moreRolesButtonContainer}>
      <Tooltip
        maxWidth={250}
        appendTo="window"
        placement="right"
        content={renderMultipleRolesTooltipContent(rolesData)}>
        <TextButton underline="onHover" className={css.moreRolesButton}>{ t('list.role.more', { count: rolesData.length }) }</TextButton>
      </Tooltip>
    </div>
  );

  return (
    <div>
      {visibleRolesTemplate}
      {restOfRolesTemplate}
    </div>
  );
};

const renderMultipleRolesTooltipContent = (roles: RolesData) => (
  <div className={css.multipleRolesTooltipContent}>
    <ul>
    {roles.map(role =>
      <li key={role.id}>
        {role.title}
        <br/>
        <Text light size="small">{role.description}</Text>
      </li>)}
    </ul>
  </div>
);

const SingleRole: React.FC<any> = ({ role, rowRole }) => {

  const [isTooltipOpen, setIsTooltipOpen] = React.useState<boolean>(false);
  return (
    <div className={css.role} key={rowRole}>
              <div data-hook="role" className={css.roleText}>
                <Text skin="standard" size="small">
                  {role.title}
                  { !!role.description && <div className={`${css.roleTooltip} ${isTooltipOpen ? css.showTooltipIcon : ''}`}>
                    <Tooltip
                      maxWidth={250}
                      appendTo="window"
                      placement="right"
                      content={role.description}
                      onShow={() => setIsTooltipOpen(true)}
                      onHide={() => setIsTooltipOpen(false)}>
                      <InfoCircleSmall style={{ color: '#3899EC' }}/>
                    </Tooltip>
                  </div> }
                </Text>
              </div>
            </div>
  );
};

export const renderUserImageCell = (rowData: TeamMemberTableRowData) => (
  <div className={css.userImageWrapper}>
    <div className={css.userImageInner}>
      { isTeamRowData(rowData) ?
        <TeamContributorImage rowData={rowData}/> :
        <UserImage profileImage={isUserRowData(rowData) ? rowData.profileImage : ''} rowData={rowData}/>
      }
    </div>
  </div>
);

export const renderNameCell = (permissions: Set<string>, locale: string, { showIcon = false, fontLargeMode = false } = {}) => (rowData: TeamMemberTableRowData) => {
  if (rowData.type === TeamTableRowType.user || rowData.type === TeamTableRowType.invite) {
    return (<UserNameCell
              title={getNameTitle(rowData)}
              subTitle={rowData.email}
              isPendingOwner={rowData.isPendingOwner}
              status={rowData.type === TeamTableRowType.invite ? rowData.status : undefined}
              fontLargeMode={fontLargeMode}
            />);
  } else {
    return (<TeamNameCell name={rowData.accountName} locale={locale} showIcon={showIcon} fontLargeMode={fontLargeMode}/>);
  }
};

export const UserNameCell: any = translate()(({ t, title, subTitle, isPendingOwner, fontLargeMode, status }) =>  (
  <div className={css.userNameCell}>
    <Box verticalAlign="middle">
      {fontLargeMode ?
        <Heading dataHook="user-name" appearance="H3">{title}</Heading> :
        <Text dataHook="user-name" size="medium" skin="standard" weight="normal" ellipsis>{title}</Text>
      }
      <div className={css.badgeWrapper}>
        {isPendingOwner && <PendingOwnerBadge/>}
        {!isPendingOwner && status === InviteStatusType.PENDING &&
          <Badge dataHook="pending-badge" size="small" skin="neutralLight">{t('list.pending')}</Badge>}
        {!isPendingOwner && status === InviteStatusType.EXPIRED &&
          <Badge dataHook="expired-badge" size="small" skin="warning" type="outlined">{t('list.expired')}</Badge>}
      </div>
    </Box>
    <Text dataHook="user-email" size={fontLargeMode ? 'medium' : 'small'} skin="standard" weight="thin" ellipsis secondary>{subTitle}</Text>
  </div>
));

const PendingOwnerBadge: any = translate()(({ t }) => <Badge dataHook="pending-ownership-badge" size="small" skin="warningLight">{t('list.pendingOwnership')}</Badge>);

export const TeamNameCell: any = translate()(({ t, name, locale, showIcon, fontLargeMode }) => (
  <div className={css.userNameCell}>
    {fontLargeMode ?
      <Heading dataHook="user-name" appearance="H3">{name}</Heading> :
      <Text dataHook="user-name" size="medium" skin="standard" weight="normal" ellipsis>{name}</Text>
    }
    <div className={css.teamInfoContainer}>
      <Text dataHook="team-text" size={fontLargeMode ? 'medium' : 'small'} skin="standard" weight="thin" ellipsis secondary >
        {t('teamDetails.team.info.title')}
      </Text>
      <div className={`${css.teamInfoTooltip} ${showIcon ? css.showIcon : ''}`}>
        <InfoIcon
        size="small"
        content={
          <Trans i18nKey="list.team.details">
            <Text light size="small" weight="normal">0</Text>
            <TextButton as="a" href={generateLearnMoreLink(locale)} skin="light" size="small" underline="always" weight="normal" target="_blank">1</TextButton>
          </Trans>
        }/>
      </div>
    </div>
  </div>
));

const renderDate = (dateString) => (
    <Text data-hook="status-date" size="small" weight="thin" secondary className={css.statusDateCell}>
        {dateString}
    </Text>
);

export const renderStatusDate = (t, locale) => (rowData: TeamMemberTableRowData) => {
    if (isUserRowData(rowData)) {
        return rowData.joinedTeamAt && renderDate(t('list.status.joined', { date: formatDate(rowData.joinedTeamAt, locale) }));
    } else if (isInviteRowData(rowData)) {
      return rowData.dateCreated && renderDate(t('list.status.invited', { date: formatDate(rowData.dateCreated, locale) }));
    }
    return '';
};

const getCurrentUserOrTeamId = (rowData: TeamMemberTableRowData): string => {
  return isInviteRowData(rowData) ? null : isUserRowData(rowData) ? rowData.userId : (rowData as TeamRowData).accountId;
};

const isCurrentUserRow = (rowData: TeamMemberTableRowData, currentUserId: string) => {
  return isUserRowData(rowData) && rowData.userId === currentUserId;
};

const isCurrentTeamContribRow = (rowData: TeamMemberTableRowData, currentAccountId: string) => {
  return isTeamRowData(rowData) && rowData.accountId === currentAccountId;
};

export const getRenderActionsCell = ({
  t,
  onRemoveRequest,
  currentUserId,
  currentAccountId,
  onGetLink,
  onResendRequest,
  permissions,
  showOwnerAction,
  siteLevel,
  isUserOwner,
  ownerId,
  onResendTransferOwnership,
  onCancelTransferOwnership,
  onGetTransferOwnershipLink,
  reportOpenChangeRolesBI,
}) => (rowData: TeamMemberTableRowData) => {

  if (ownerId === getCurrentUserOrTeamId(rowData)) {
    return (showOwnerAction && <TeamMembersOwnerActionCell/>);
  } else if (isCurrentUserRow(rowData, currentUserId) || isCurrentTeamContribRow(rowData, currentAccountId)) {
    if (hasPermissionByLevel(siteLevel, PermissionKey.DELETE_SELF, permissions)) {
      return (<Button size="small" skin="inverted" prefixIcon={<UserRemoveSmall />} onClick={() => onRemoveRequest(getCurrentUserOrTeamId(rowData), true)}>{t(siteLevel ? 'list.teamMemberActions.site.leave' : 'list.teamMemberActions.leaveTeam')}</Button>);
    } else {
      return null;
    }
  } else {
    return (<div className={css.actionsWrapper}>
      <TeamMemberActions rowData={rowData}
                         onRemoveRequest={onRemoveRequest}
                         onResendRequest={onResendRequest}
                         onGetLink={onGetLink}
                         permissions={permissions}
                         siteLevel={siteLevel}
                         isUserOwner={isUserOwner}
                         onResendTransferOwnership={onResendTransferOwnership}
                         onCancelTransferOwnership={onCancelTransferOwnership}
                         onGetTransferOwnershipLink={onGetTransferOwnershipLink}
                         reportOpenChangeRolesBI={reportOpenChangeRolesBI}/>
    </div>);
  }
};

export const generateRolesFiltersOption = (roleIdsLength, roles) => roleId => {
  const roleTitle = roles.find(role => role.id === roleId)?.title;
  return {
    id: roleId,
    roleTitle,
    inputTitle: roleId,
    value: (<div className={css.roleFilterRow}><span title={roleTitle}>{roleTitle}</span><Text light secondary className={css.badge}>{roleIdsLength[roleId]}</Text></div>)
  };
};

export const getPendingMembers = member => member.type === TeamTableRowType.invite && member.status === InviteStatusType.PENDING;
export const getExpiredMembers = member => member.type === TeamTableRowType.invite && member.status === InviteStatusType.EXPIRED;
export const getPendingOwners = member => member.isPendingOwner;

export const StatusFilterSuffix = translate()(({ t }) => {
  return (
    <Tooltip
      size="small"
      appendTo="window"
      content={t('teamDetails.actions.statusFilter.pendingWarning')}>
      <WarningIcon className={css.statusFilterSuffix} />
    </Tooltip>
  );
});
